// ForceDirectedView.tsx
// React component for rendering the force-directed graph layout

import React, { useCallback, useEffect, useRef } from 'react';
import * as d3 from 'd3';
import type { NodeInstance, RelCreationState } from '../types';
import { getNodeBaseColor, scoreToHighlightColor } from '../utils/colors';

const PREVIEW_ACCENT = '#ff4fd8';

const resolveNodeBaseColor = (node: NodeInstance): string => {
  if (node.color && node.color.trim() !== '') {
    return node.color;
  }
  return getNodeBaseColor(undefined);
};

// ---- Module-level helpers (no closure over component state) ----------------

/** Returns a d3 drag behaviour that heats/cools the simulation via simRef. */
function makeDrag(
  simRef: React.MutableRefObject<d3.Simulation<SimNode, SimLink> | null>
): d3.DragBehavior<SVGGElement, SimNode, SimNode | d3.SubjectPosition> {
  return d3
    .drag<SVGGElement, SimNode>()
    .on('start', (event: d3.D3DragEvent<SVGGElement, SimNode, SimNode>) => {
      if (!event.active) simRef.current?.alphaTarget(0.3).restart();
      event.subject.fx = event.subject.x;
      event.subject.fy = event.subject.y;
    })
    .on('drag', (event: d3.D3DragEvent<SVGGElement, SimNode, SimNode>) => {
      event.subject.fx = event.x;
      event.subject.fy = event.y;
    })
    .on('end', (event: d3.D3DragEvent<SVGGElement, SimNode, SimNode>) => {
      if (!event.active) simRef.current?.alphaTarget(0);
      event.subject.fx = null;
      event.subject.fy = null;
    });
}

// ---- Component --------------------------------------------------------------

type SimNode = NodeInstance & d3.SimulationNodeDatum;

// D3 link shape: source/target start as string IDs, become SimNode refs after simulation init
interface SimLink extends d3.SimulationLinkDatum<SimNode> {
  type: string;
  id: string;
  from_node_id: string;
  to_node_id: string;
}

interface ForceDirectedViewProps {
  graphData: {
    nodes: NodeInstance[];
    relationships: Array<{ id: string; type: string; from_node_id: string; to_node_id: string }>;
  } | null;
  relCreation: RelCreationState;
  onNodeClick: (node: NodeInstance) => void;
  onNodeContextMenu?: (
    node: { id: string; name?: string },
    position: { x: number; y: number }
  ) => void;
  onBackgroundClick?: () => void;
  simulationRef?: React.MutableRefObject<d3.Simulation<SimNode, SimLink> | null>;
  /** Highlighted nodes. The focus node is passed here so it stays visually distinct. */
  highlightedNodeScores?: Map<string, number>;
  /** IDs of nodes that exist only in the current preview graph. */
  previewNodeIds?: Set<string>;
  /** Relationship keys (`from|to|type`) that exist only in the current preview graph. */
  previewRelationshipKeys?: Set<string>;
  /** Incrementing key that forces a full layout reset (clears saved positions). */
  layoutKey?: number;
}

const ForceDirectedView: React.FC<ForceDirectedViewProps> = ({
  graphData,
  relCreation,
  onNodeClick,
  onNodeContextMenu,
  onBackgroundClick,
  simulationRef,
  highlightedNodeScores = new Map(),
  previewNodeIds = new Set(),
  previewRelationshipKeys = new Set(),
  layoutKey = 0,
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const gRef = useRef<SVGGElement>(null);
  const zoomTransformRef = useRef<d3.ZoomTransform>(d3.zoomIdentity);
  const onNodeClickRef = useRef(onNodeClick);
  const onNodeContextMenuRef = useRef(onNodeContextMenu);
  const onBackgroundClickRef = useRef(onBackgroundClick);

  useEffect(() => {
    onNodeClickRef.current = onNodeClick;
  }, [onNodeClick]);

  useEffect(() => {
    onNodeContextMenuRef.current = onNodeContextMenu;
  }, [onNodeContextMenu]);

  useEffect(() => {
    onBackgroundClickRef.current = onBackgroundClick;
  }, [onBackgroundClick]);

  const isPreviewLink = useCallback(
    (link: { from_node_id: string; to_node_id: string; type: string }): boolean =>
      previewRelationshipKeys.has(`${link.from_node_id}|${link.to_node_id}|${link.type}`),
    [previewRelationshipKeys]
  );

  const getNodeRadius = useCallback(
    (node: SimNode): number => (previewNodeIds.has(node.id) ? 28 : 20),
    [previewNodeIds]
  );

  const getNodeFill = useCallback(
    (node: SimNode): string =>
      previewNodeIds.has(node.id) ? '#ff4fd8' : resolveNodeBaseColor(node),
    [previewNodeIds]
  );

  const getNodeStroke = useCallback(
    (node: SimNode): string => {
      const score = highlightedNodeScores.get(node.id);
      if (score !== undefined) return scoreToHighlightColor(score);
      return previewNodeIds.has(node.id) ? '#fff3a3' : '#fff';
    },
    [highlightedNodeScores, previewNodeIds]
  );

  const getNodeStrokeWidth = useCallback(
    (node: SimNode): number => {
      const score = highlightedNodeScores.get(node.id);
      // Strongest match gets the thicker ring so it reads as the focused node.
      if (score !== undefined) return score >= 0.9 ? 4 : 2.5;
      return previewNodeIds.has(node.id) ? 4 : 2;
    },
    [highlightedNodeScores, previewNodeIds]
  );

  // Persistent simulation and group refs — survive graphData re-renders
  const localSimRef = useRef<d3.Simulation<SimNode, SimLink> | null>(null);
  const linkGroupRef = useRef<d3.Selection<SVGGElement, unknown, null, undefined> | null>(null);
  const nodeGroupRef = useRef<d3.Selection<SVGGElement, unknown, null, undefined> | null>(null);
  // Track layoutKey so we can detect when a full re-layout is requested
  const layoutKeyRef = useRef(layoutKey);

  // ── One-time initialisation: zoom, arrowhead marker, persistent <g> groups ──
  useEffect(() => {
    if (!svgRef.current || !gRef.current) return;

    const svgElement = svgRef.current;
    const d3svg = d3.select(svgElement);
    const g = d3.select(gRef.current);

    d3svg.call(
      d3
        .zoom<SVGSVGElement, unknown>()
        .scaleExtent([0.1, 4])
        .on('zoom', (event) => {
          g.attr('transform', event.transform);
          zoomTransformRef.current = event.transform;
        })
    );

    d3svg
      .append('defs')
      .append('marker')
      .attr('id', 'arrowhead')
      .attr('viewBox', '-0 -5 10 10')
      .attr('refX', 25)
      .attr('refY', 0)
      .attr('orient', 'auto')
      .attr('markerWidth', 8)
      .attr('markerHeight', 8)
      .attr('xoverflow', 'visible')
      .append('svg:path')
      .attr('d', 'M 0,-5 L 10 ,0 L 0,5')
      .attr('fill', '#999')
      .style('stroke', 'none');

    d3svg
      .select('defs')
      .append('marker')
      .attr('id', 'arrowhead-preview')
      .attr('viewBox', '-0 -5 10 10')
      .attr('refX', 25)
      .attr('refY', 0)
      .attr('orient', 'auto')
      .attr('markerWidth', 8)
      .attr('markerHeight', 8)
      .attr('xoverflow', 'visible')
      .append('svg:path')
      .attr('d', 'M 0,-5 L 10 ,0 L 0,5')
      .attr('fill', PREVIEW_ACCENT)
      .style('stroke', 'none');

    linkGroupRef.current = g.append('g').attr('class', 'link-group');
    nodeGroupRef.current = g.append('g').attr('class', 'node-group');

    return () => {
      localSimRef.current?.stop();
      localSimRef.current = null;
      // Remove DOM elements so a remount (React StrictMode, HMR) starts clean
      d3.select(svgElement).select('defs').remove();
      g.selectAll('*').remove();
      linkGroupRef.current = null;
      nodeGroupRef.current = null;
    };
  }, []); // runs once on mount

  // ── Incremental data update — NEVER tears down the whole graph ──
  useEffect(() => {
    if (!graphData || !svgRef.current || !linkGroupRef.current || !nodeGroupRef.current) return;

    const linkGroup = linkGroupRef.current;
    const nodeGroup = nodeGroupRef.current;

    // Detect whether a full re-layout was requested (layoutKey changed)
    const fullRelayout = layoutKey !== layoutKeyRef.current;
    layoutKeyRef.current = layoutKey;

    // Collect current positions from the running simulation so existing nodes keep their spot
    const currentPositions = new Map<string, { x: number; y: number }>();
    if (!fullRelayout) {
      localSimRef.current?.nodes().forEach((n) => {
        if (n.x != null && n.y != null) currentPositions.set(n.id, { x: n.x, y: n.y });
      });
    }

    const links: SimLink[] = (graphData.relationships || []).map((rel) => ({
      source: rel.from_node_id,
      target: rel.to_node_id,
      type: rel.type,
      id: rel.id,
      from_node_id: rel.from_node_id,
      to_node_id: rel.to_node_id,
    }));

    const cx = svgRef.current.clientWidth / 2;
    const cy = svgRef.current.clientHeight / 2;
    const adjacency = new Map<string, string[]>();
    links.forEach((link) => {
      const fromNeighbors = adjacency.get(link.from_node_id) ?? [];
      fromNeighbors.push(link.to_node_id);
      adjacency.set(link.from_node_id, fromNeighbors);

      const toNeighbors = adjacency.get(link.to_node_id) ?? [];
      toNeighbors.push(link.from_node_id);
      adjacency.set(link.to_node_id, toNeighbors);
    });

    const nodes: SimNode[] = (graphData.nodes || []).map((n) => {
      const saved = currentPositions.get(n.id);
      if (saved) return { ...n, x: saved.x, y: saved.y } as SimNode;

      const parentId = (n as NodeInstance & { parent_id?: string }).parent_id;
      const neighborIds = [...(parentId ? [parentId] : []), ...(adjacency.get(n.id) ?? [])];
      const anchor = neighborIds
        .map((neighborId) => currentPositions.get(neighborId))
        .find((position): position is { x: number; y: number } => position != null);

      return {
        ...n,
        x: (anchor?.x ?? cx) + (Math.random() - 0.5) * 40,
        y: (anchor?.y ?? cy) + (Math.random() - 0.5) * 40,
      } as SimNode;
    });

    // Create simulation on first data load or full re-layout; update incrementally otherwise
    if (!localSimRef.current || fullRelayout) {
      localSimRef.current?.stop();
      localSimRef.current = d3
        .forceSimulation<SimNode, SimLink>(nodes)
        .force(
          'link',
          d3
            .forceLink<SimNode, SimLink>(links)
            .id((d) => d.id)
            .distance(100)
        )
        .force('charge', d3.forceManyBody().strength(-300))
        .force('center', d3.forceCenter(cx, cy))
        .force('collision', d3.forceCollide().radius(40));
    } else {
      // Feed updated data into the existing simulation — existing node objects
      // already carry their x/y so positions are preserved
      localSimRef.current.nodes(nodes);
      (localSimRef.current.force('link') as d3.ForceLink<SimNode, SimLink>).links(links);
      // Low alpha so only new/moved nodes settle; existing ones barely shift
      localSimRef.current.alpha(0.1).alphaDecay(0.08).restart();
    }

    const simulation = localSimRef.current;
    if (simulationRef) simulationRef.current = simulation;

    // ── Incremental link join ──
    linkGroup
      .selectAll<SVGLineElement, SimLink>('.graph-link')
      .data(links, (d) => `${d.from_node_id}-${d.to_node_id}-${d.type}`)
      .join(
        (enter) =>
          enter
            .append('line')
            .attr('class', 'graph-link')
            .attr('stroke', (d) => (isPreviewLink(d) ? PREVIEW_ACCENT : '#999'))
            .attr('stroke-opacity', (d) => (isPreviewLink(d) ? 0.95 : 0.6))
            .attr('stroke-width', (d) => (isPreviewLink(d) ? 4 : 2))
            .attr('marker-end', (d) => (isPreviewLink(d) ? 'url(#arrowhead-preview)' : 'url(#arrowhead)')),
        (update) =>
          update
            .attr('stroke', (d) => (isPreviewLink(d) ? PREVIEW_ACCENT : '#999'))
            .attr('stroke-opacity', (d) => (isPreviewLink(d) ? 0.95 : 0.6))
            .attr('stroke-width', (d) => (isPreviewLink(d) ? 4 : 2))
            .attr('marker-end', (d) => (isPreviewLink(d) ? 'url(#arrowhead-preview)' : 'url(#arrowhead)')),
        (exit) => exit.remove()
      );

    linkGroup
      .selectAll<SVGTextElement, SimLink>('.link-label')
      .data(links, (d) => `${d.from_node_id}-${d.to_node_id}-${d.type}`)
      .join(
        (enter) =>
          enter
            .append('text')
            .attr('class', 'link-label')
            .attr('font-size', 10)
            .attr('fill', (d) => (isPreviewLink(d) ? PREVIEW_ACCENT : '#666'))
            .text((d) => d.type),
          (update) => update.attr('fill', (d) => (isPreviewLink(d) ? PREVIEW_ACCENT : '#666')).text((d) => d.type),
        (exit) => exit.remove()
      );

    // ── Incremental node join ──
    nodeGroup
      .selectAll<SVGGElement, SimNode>('.graph-node')
      .data(nodes, (d) => d.id)
      .join(
        (enter) => {
          const n = enter.append('g').attr('class', 'graph-node');

          n.append('circle')
            .attr('r', (d) => getNodeRadius(d))
            .attr('fill', (d) => getNodeFill(d))
            .attr('stroke', (d) => getNodeStroke(d))
            .attr('stroke-width', (d) => getNodeStrokeWidth(d));

          n.append('text')
            .attr('dy', 35)
            .attr('text-anchor', 'middle')
            .attr('font-size', 12)
            .attr('fill', '#333')
            .text((d) => d.name || d.id);

          // Click reads relCreationRef so stale closures are not a problem
          n.on('click', function (event: MouseEvent, d: SimNode) {
            event.preventDefault();
            event.stopPropagation();
            onNodeClickRef.current(d);
          });

          n.on('contextmenu', function (event: MouseEvent, d: SimNode) {
            event.preventDefault();
            event.stopPropagation();
            onNodeContextMenuRef.current?.(
              { id: d.id, name: d.name },
              { x: event.clientX, y: event.clientY }
            );
          });

          n.call(makeDrag(localSimRef));
          n.on('mousedown.zoom', (event: MouseEvent) => event.stopPropagation());

          return n;
        },
        (update) => {
          // Update label/colour for changed nodes without recreating the element
          update.select('text').text((d: SimNode) => d.name || d.id);
          update
            .select('circle')
            .attr('r', (d: SimNode) => getNodeRadius(d))
            .attr('fill', (d: SimNode) => getNodeFill(d))
            .attr('stroke', (d: SimNode) => getNodeStroke(d))
            .attr('stroke-width', (d: SimNode) => getNodeStrokeWidth(d));
          return update;
        },
        (exit) => exit.remove()
      );

    // Tick handler points at latest group selections
    simulation.on('tick', () => {
      linkGroup
        .selectAll<SVGLineElement, SimLink>('.graph-link')
        .attr('x1', (d) => (d.source as SimNode).x ?? 0)
        .attr('y1', (d) => (d.source as SimNode).y ?? 0)
        .attr('x2', (d) => (d.target as SimNode).x ?? 0)
        .attr('y2', (d) => (d.target as SimNode).y ?? 0);

      linkGroup
        .selectAll<SVGTextElement, SimLink>('.link-label')
        .attr('x', (d) => (((d.source as SimNode).x ?? 0) + ((d.target as SimNode).x ?? 0)) / 2)
        .attr('y', (d) => (((d.source as SimNode).y ?? 0) + ((d.target as SimNode).y ?? 0)) / 2);

      nodeGroup
        .selectAll<SVGGElement, SimNode>('.graph-node')
        .attr('transform', (d) => `translate(${d.x ?? 0},${d.y ?? 0})`);
    });
  }, [
    graphData,
    getNodeFill,
    getNodeRadius,
    getNodeStroke,
    getNodeStrokeWidth,
    isPreviewLink,
    layoutKey,
    simulationRef,
  ]); // relCreation handled by separate effect below

  // Node class update effect (for relCreation highlights)
  useEffect(() => {
    if (!svgRef.current || !graphData) return;
    d3.select(svgRef.current)
      .selectAll<SVGGElement, SimNode>('.graph-node')
      .attr('class', (d) => {
        let base = 'graph-node';
        if (relCreation.active && relCreation.allowedTargets.includes(d.id))
          base += ' allowed-target';
        if (relCreation.active && relCreation.sourceNode && d.id === relCreation.sourceNode.id)
          base += ' rel-source';
        if (
          relCreation.active &&
          !relCreation.allowedTargets.includes(d.id) &&
          (!relCreation.sourceNode || d.id !== relCreation.sourceNode.id)
        )
          base += ' disabled-node';
        return base;
      });
  }, [relCreation, graphData]);

  // Focus highlight effect — keep the focused node orange without recolouring other search results.
  useEffect(() => {
    if (!svgRef.current || !graphData) return;
    d3.select(svgRef.current)
      .selectAll<SVGGElement, SimNode>('.graph-node')
      .select('circle')
      .attr('fill', (d: SimNode) => {
        if (previewNodeIds.has(d.id)) return '#ff4fd8';
        const score = highlightedNodeScores.get(d.id);
        return score !== undefined ? scoreToHighlightColor(score) : resolveNodeBaseColor(d);
      })
      .attr('opacity', 1.0)
      .attr('stroke', (d: SimNode) => {
        const score = highlightedNodeScores.get(d.id);
        if (score !== undefined) return scoreToHighlightColor(score);
        return previewNodeIds.has(d.id) ? '#fff3a3' : '#fff';
      })
      .attr('stroke-width', (d: SimNode) => {
        const score = highlightedNodeScores.get(d.id);
        if (score !== undefined) return score >= 0.9 ? 4 : 2.5;
        return previewNodeIds.has(d.id) ? 4 : 2;
      });
  }, [highlightedNodeScores, graphData, previewNodeIds]);

  return (
    <svg
      ref={svgRef}
      className="graph-svg"
      width="100%"
      height="100%"
      onClick={() => onBackgroundClickRef.current?.()}
    >
      <g ref={gRef} />
    </svg>
  );
};

export default ForceDirectedView;
